import React, { useState, useEffect } from 'react';
import InsightsIcon from '@mui/icons-material/Insights';
import _ from 'underscore';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import DeleteIcon from '@mui/icons-material/Delete';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import Visibility from '@mui/icons-material/Visibility';

import Alert from '@mui/material/Alert';
import { Table, TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import createTracks from "./createTracks";
import createDefaultSession from './createDefaultSession';
import EventBus from "../common/EventBus";
import socketIOClient from 'socket.io-client';
import socketUrl from '../common/GetSocket';
import { Redirect } from "react-router-dom";
import JBrowseService from '../services/jbrowse.service';
import CircosPlot from './CircosPlot';

const ENDPOINT = socketUrl();

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const BlastModal = (props) => {
  let {
    blastData,
    setBlastData,
    blastModalIsOpen,
    setBlastModalIsOpen,
    navigateToLocation,
    setLoading,
    blastParagraph,
    setBlastParagraph,
    currentUser,
    blastLoading,
    setBlastLoading,
    chromosomes,
    location,
    currentAssembly,
    selectedCrop,
    selectedTracksRef,
    setSelectedTracks,
    setCurrentTracks,
    setCurrentSession,
    updateTracks,
    setShowCircos,
  } = props;

  const [spinner, setSpinner] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [value, setValue] = useState(0);
  const [sequence, setSequence] = useState("");
  const [sequenceType, setSequenceType] = useState('dna');
  const [shortRead, setShortRead] = useState('normal');
  const [chromosome, setChromosome] = useState(location.split(":")[0]);
  const [start, setStart] = useState(0);
  const [stop, setStop] = useState(0);
  const [blastWarning, setBlastWarning] = useState(false);

  const totalPages = blastData.length > 0 ? Math.ceil(blastData.length / itemsPerPage) : 0;
  const headers = blastData.length > 0 ? Object.keys(blastData[0]) : [];
  headers.unshift("Jump");
  const paginatedData = blastData.length > 0
    ? blastData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : [];

  useEffect(() => {
    if (blastModalIsOpen) {
      const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
          closeModal();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [blastModalIsOpen]);

  useEffect(() => {
    const socket = socketIOClient(ENDPOINT);
    socket.on("ai_response", response => {
      if (currentUser.email === response.email) {
        setBlastParagraph(response.result);
        setBlastLoading(false);
      }
    });
    return () => {
      socket.disconnect();
    };
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const validateOrBuildQuery = (data) => {
    const queryPattern = /^[\w:]+\:\d+-\d+$/;
    const { query, chromosome, start_subject, end_subject } = data;
    if (queryPattern.test(query)) {
      return query; 
    }
    return `${chromosome}:${start_subject}-${end_subject}`;
  };

  const jumpToLocation = (locationData) => {
    const validatedQuery = validateOrBuildQuery(locationData);
    setLoading(true);
    navigateToLocation(validatedQuery);
    setTimeout(() => {
      setBlastModalIsOpen(false);
      setLoading(false);
    }, 1000);
  };

  const blastRegion = (event) => {
    event.preventDefault();
    setLoading(true);
    setSpinner( true );

    if (sequence === "") {
      const obj = {
        starting: start,
        ending: stop,
        chromosome: chromosome,
        genome: currentAssembly,
        crop: selectedCrop,
        user: currentUser.username,
        company: currentUser.DirectoryName
      };
      setBlastLoading(true);
      setBlastParagraph("");

      async function handleBlastRequest() {
        try {
          const response = await JBrowseService.blastRegion(obj);
          const filteredTracks = selectedTracksRef.current.filter(item => !item.includes("BlastJob"));
          const tracks = [...new Set([...filteredTracks, ...response.files])];
          setSelectedTracks(tracks);
          const newTracks = await createTracks(currentAssembly, tracks);
          const cleanTracks = newTracks.filter(item => item !== undefined);
          const trackArray = _.pluck(cleanTracks, 'name');
          setCurrentTracks(newTracks);
          const newSession = createDefaultSession(currentAssembly, trackArray);
          setCurrentSession(newSession);
          setTimeout(() => {
            updateTracks(cleanTracks, newSession);
            setBlastData(response.result);
            setLoading(false);
            setSpinner(false);
            setBlastLoading(false);
          }, 500);
        } catch (error) {
          setLoading(false);
          setSpinner(false);
          if (error.code === "ERR_NETWORK") {
            EventBus.dispatch("logout");
          }
        }
      }
      handleBlastRequest();
    } else {
      const obj = {
        type: sequenceType === 'protein',
        short: shortRead === 'short',
        sequence: sequence,
        genome: currentAssembly,
        user: currentUser.email,
        company: currentUser.DirectoryName,
      };
      setBlastLoading(true);
      setBlastParagraph("");

      async function handleBlastSequenceRequest() {
        try {
          const response = await JBrowseService.blastSequence(obj);
          if (response.result.length === 0) {
            setLoading(false);
            setSpinner(false);
            setBlastLoading(false);
            setBlastWarning(true);
            setTimeout(() => {
              setBlastWarning(false);
            }, 3000);
          } else {
            const filteredTracks = selectedTracksRef.current.filter(item => !item.includes("BlastJob"));
            const tracks = [...new Set([...filteredTracks, ...response.files])];
            setSelectedTracks(tracks);
            const newTracks = await createTracks(currentAssembly, tracks);
            const cleanTracks = newTracks.filter(item => item !== undefined);
            const trackArray = _.pluck(cleanTracks, 'name');
            setCurrentTracks(newTracks);
            const newSession = createDefaultSession(currentAssembly, trackArray);
            setCurrentSession(newSession);
            setTimeout(() => {
              updateTracks(cleanTracks, newSession);
              setBlastData(response.result);
              setLoading(false);
              setSpinner(false);
              setBlastLoading(false);
            }, 250);
          }
        } catch (error) {
          setLoading(false);
          setSpinner(false);
          setBlastLoading(false);
          if (error.code === "ERR_NETWORK") {
            EventBus.dispatch("logout");
          }
        }
      }
      handleBlastSequenceRequest();
      setLoading(false);
    }
  };

  const clearBlastResults = (event) => {
    event.preventDefault();
    setLoading(true);
    setBlastParagraph("");
    setBlastData([]);
    setTimeout(() => {
      setLoading(false);
    }, 50);
  };

  const handleCircosPlot = () => {
    setBlastModalIsOpen(false);
    setTimeout( () => {
      setShowCircos( true );
    },100);
  }

  const closeModal = () => {
    setBlastModalIsOpen(false);
    setCurrentPage(1);
  };

  return (
    <>
    <Dialog
      open={blastModalIsOpen}
      onClose={closeModal}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        elevation: 5,
        sx: { overflowY: 'visible', boxShadow: 10 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 4 }}>
        Blast Feature
          {blastData.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, mr: 4 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Showing {Math.min(blastData.length, itemsPerPage)} of{' '}
                {blastData.length.toLocaleString()} results
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontFeatureSettings: "'tnum' on, 'lnum' on" }}
              >
                ({itemsPerPage} per page)
              </Typography>
            </Box>
          )}

        <IconButton
          aria-label="close"
          onClick={closeModal}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
      {/* Blast  Table */}
        {blastData.length > 0 ? (
          <>
            <Box sx={{ overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {headers.map((header) => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((item, index) => (
                    <TableRow key={index}>
                      {headers.map((header) =>
                        header === 'Jump' ? (
                          <TableCell key="jump-button">
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => jumpToLocation(item)}
                              fullWidth
                            >
                              Jump
                            </Button>
                          </TableCell>
                        ) : (
                          <TableCell key={header}>{item[header]}</TableCell>
                        )
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              {currentPage > 1 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
              )}
              <Typography variant="body2">
                Page {currentPage} of {totalPages}
              </Typography>
              {currentPage < totalPages && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              )}
            </Box>
          </>
        ) : (
          // Blast Form
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                <Tab label="Sequence Input" {...a11yProps(0)} />
                <Tab label="Location Input" {...a11yProps(1)} />
              </Tabs>
            </Box>
            <TabPanel value={value} index={0}>
              <FormControl fullWidth>
                <TextField
                  label="Sequence"
                  multiline
                  minRows={4}
                  maxRows={10}
                  placeholder="Enter sequence here"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={sequence}
                  onChange={(e) => setSequence(e.target.value)}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontFamily: '"Roboto Mono", monospace',
                      fontSize: '0.875rem',
                      overflow: 'auto',
                      scrollbarWidth: 'thin',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f5f5f5',  // Equivalent to theme.palette.grey[100]
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#bdbdbd',  // Equivalent to theme.palette.grey[400]
                        borderRadius: '4px',
                        '&:hover': {
                          background: '#9e9e9e'  // Darker grey on hover
                        }
                      },
                    }
                  }}
                  inputProps={{
                    style: {
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={sequenceType === 'dna'}
                      onChange={() => setSequenceType(sequenceType === 'dna' ? 'protein' : 'dna')}
                    />
                  }
                  label={sequenceType.toUpperCase()}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={shortRead === 'short'}
                      onChange={() => setShortRead(shortRead === 'short' ? 'normal' : 'short')}
                    />
                  }
                  label={shortRead.toUpperCase()}
                />
              </FormControl>
            </TabPanel>

            <TabPanel value={value} index={1}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="chromosome-select-label">Chromosome</InputLabel>
                <Select
                  labelId="chromosome-select-label"
                  id="chromosome-select"
                  value={chromosome}
                  label="Chromosome"
                  onChange={(e) => setChromosome(e.target.value)}
                >
                  {chromosomes.map((chr) => (
                    <MenuItem key={chr} value={chr}>
                      {chr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Start Location"
                type="number"
                variant="outlined"
                fullWidth
                margin="normal"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
              <TextField
                label="Stop Location"
                type="number"
                variant="outlined"
                fullWidth
                margin="normal"
                value={stop}
                onChange={(e) => setStop(e.target.value)}
              />
            </TabPanel>
          </Box>
        )}

        {spinner && (
            <div className="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
        )}

        {blastWarning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            There were no Blast hits for that sequence. Try Again
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
            {blastData.length > 0 && (
              <>
                <Button
                    variant="contained"
                    color="inherit"
                    sx={{ mr: 2, bgcolor: 'grey.500', color: 'white', '&:hover': { bgcolor: 'grey.600' } }}
                    startIcon={<DeleteIcon />}
                    onClick={clearBlastResults}
                >
                    Clear Blast Results
                </Button>

                <Button
                  variant="contained"
                  color="inherit"
                  sx={{ mr: 2, bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' }}}
                  startIcon={<Visibility />}
                  onClick={ handleCircosPlot }
                >
                  Show Circos Plot
                </Button>
              </>
            )}
        </Box>

        <Box>
          {blastData.length === 0 && (
            <Button
              variant="contained"
              color="primary"
              sx={{ mr: 2 }}
              startIcon={<BlurOnIcon />}
              onClick={blastRegion}
            >
              Blast
            </Button>
          )}
          <Button
            variant="outlined"
            color="inherit"
            onClick={closeModal}
          >
            Close
          </Button>
        </Box>
          
      </DialogActions>
    </Dialog>


    </>
  );
};

export default React.memo(BlastModal);
